import React from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import './Blog.css';

const blogPosts = [
  {
    slug: 'bringing-healing-energy-to-sydneys-north-shore',
    title: "Bringing Healing Energy to Sydney's North Shore",
    excerpt: "Discover how holistic healing practices are transforming wellness on Sydney's North Shore. From Reiki to meditation, explore the growing movement towards natural, energy-based healing.",
    date: '2024-08-15',
    category: 'Wellness',
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&q=80',
    content: `Sydney's North Shore has long been known for its beautiful harbourside living and vibrant community spirit. Now, a growing number of residents are discovering the transformative power of holistic healing practices.

As a holistic health practitioner based in Sydney, I've had the privilege of bringing Reiki healing, meditation, and energy work to individuals and families across the North Shore. The response has been incredibly heartwarming — people are genuinely seeking deeper connections with their wellbeing.

## Why Holistic Healing?

In our fast-paced modern lives, many of us carry stress, tension, and emotional weight that traditional approaches alone may not fully address. Holistic healing works with your body's natural energy systems to promote balance, relaxation, and deep restoration.

Whether it's through Reiki energy healing, guided meditation, or chakra balancing, these practices offer a complementary path to wellness that honours the whole person — mind, body, and spirit.

## What to Expect

If you're new to holistic healing, know that each session is tailored to your unique needs. We begin with a conversation about what's present for you, and from there, I intuitively guide the session to support your healing journey.

Many clients report feeling lighter, calmer, and more centred after just one session. Regular sessions can support ongoing emotional balance, stress relief, and personal growth.

## Join the Movement

If you're on Sydney's North Shore and curious about holistic healing, I invite you to reach out. Whether you're dealing with stress, seeking clarity, or simply wanting to explore something new, there's a place for you here.`
  },
  {
    slug: 'finding-forgiveness',
    title: 'Finding Forgiveness: A Path to Inner Peace',
    excerpt: "Forgiveness isn't about condoning what happened — it's about freeing yourself from the weight of carrying it. Explore how energy healing can support your journey to forgiveness and inner peace.",
    date: '2024-06-20',
    category: 'Healing',
    image: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=800&q=80',
    content: `Forgiveness is one of the most powerful yet challenging journeys we can undertake. It's not about forgetting or excusing what happened — it's about releasing the energetic burden that keeps us tethered to pain.

## The Weight of Unforgiveness

When we hold onto resentment, anger, or hurt, it doesn't just live in our minds. It settles into our bodies, our energy fields, and our daily lives. You might notice it as tension in your shoulders, a heaviness in your chest, or a persistent feeling of being stuck.

From an energetic perspective, unforgiveness creates blockages that prevent life force energy from flowing freely. This can manifest as fatigue, anxiety, difficulty sleeping, or even physical ailments.

## How Energy Healing Supports Forgiveness

Through Reiki and other energy healing modalities, we can gently begin to release these blockages. The healing energy works at a deep level, addressing not just the symptoms but the root causes of energetic stagnation.

During a healing session focused on forgiveness, you may experience:
- A release of emotions that have been stored in your body
- A sense of lightness as old energy patterns shift
- Greater clarity about your situation and feelings
- A natural softening towards yourself and others

## The Journey, Not the Destination

Forgiveness is rarely a single moment — it's a process. Be gentle with yourself as you navigate this path. Some days will feel easier than others, and that's completely normal.

What matters is that you've chosen to begin. And in that choice, there is already healing.

If you're ready to explore forgiveness through energy healing, I'm here to walk alongside you on this journey.`
  },
  {
    slug: 'understanding-reiki-healing',
    title: 'Understanding Reiki Healing: What It Is and How It Works',
    excerpt: "Reiki is a gentle, non-invasive form of energy healing that promotes relaxation and supports the body's natural healing processes. Learn more about this ancient practice.",
    date: '2024-04-10',
    category: 'Reiki',
    image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&q=80',
    content: `Reiki is a Japanese healing technique that channels universal life force energy through the practitioner's hands to the recipient. The word "Reiki" comes from two Japanese words: "Rei" (universal) and "Ki" (life energy).

## How Does Reiki Work?

Reiki works on the principle that life force energy flows through all living things. When this energy is low or blocked, we're more likely to feel stressed, unwell, or out of balance. When it flows freely, we feel healthy, vibrant, and at peace.

During a Reiki session, the practitioner acts as a channel for this universal energy, directing it to where your body needs it most. The energy naturally flows to areas of imbalance, promoting healing on physical, emotional, mental, and spiritual levels.

## What Happens During a Session?

A typical Reiki session involves lying comfortably on a massage table, fully clothed. The practitioner places their hands on or just above various positions on the body. You may feel warmth, tingling, or a sense of deep relaxation.

Many people describe the experience as profoundly calming — like being wrapped in a warm, gentle light. Some people see colours, experience emotional release, or simply drift into a peaceful meditative state.

## Benefits of Regular Reiki

- Deep relaxation and stress reduction
- Support for the body's natural healing processes
- Release of energy blockages
- Emotional balance and clarity
- Better sleep quality
- Enhanced overall wellbeing

## Is Reiki Right for You?

Reiki is suitable for everyone, regardless of age or health condition. It complements conventional medicine and can be used alongside other treatments. Whether you're dealing with a specific health concern or simply seeking greater balance in your life, Reiki has something to offer.`
  },
  {
    slug: 'benefits-of-meditation',
    title: 'The Transformative Benefits of Regular Meditation Practice',
    excerpt: 'Meditation is more than just sitting quietly — it is a powerful tool for mental clarity, emotional balance, and spiritual growth. Discover how a regular practice can transform your life.',
    date: '2024-02-28',
    category: 'Meditation',
    image: 'https://images.unsplash.com/photo-1528715471579-d1bcf0ba5e83?w=800&q=80',
    content: `In a world that constantly demands our attention, meditation offers a sanctuary of stillness. But beyond the quiet, meditation is a practice with profound, scientifically-backed benefits that can transform every aspect of your life.

## The Science Behind Meditation

Research has consistently shown that regular meditation practice can:
- Reduce cortisol levels (the stress hormone)
- Lower blood pressure and heart rate
- Improve focus and concentration
- Enhance emotional regulation
- Boost immune function
- Increase grey matter in the brain

## Getting Started

You don't need to meditate for hours to experience benefits. Even 10-15 minutes a day can make a meaningful difference. Here are some tips for beginners:

1. **Start small** — Begin with just 5 minutes and gradually increase
2. **Be consistent** — Same time, same place helps build the habit
3. **Be patient** — Your mind will wander, and that's perfectly normal
4. **Use guidance** — Guided meditations can help you find your rhythm

## Meditation and Energy Healing

At KO Holistic Health, we often incorporate meditation into our healing sessions. The combination of Reiki energy and meditative practice creates a deeply restorative experience that addresses both the energetic and mental aspects of wellbeing.

Our Restorative Reiki Classes, limited to just 5 participants, offer a beautiful group meditation and healing experience in an intimate setting.

Whether you're a complete beginner or an experienced meditator, there's always room to deepen your practice and discover new layers of inner peace.`
  }
];

const BlogList = () => {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <>
      <SEO
        title="Blog | Holistic Health Insights | KO Holistic Health Sydney"
        description="Explore articles on Reiki healing, meditation, chakra balancing, spiritual growth and holistic wellness from Kirstie Warburton at KO Holistic Health Sydney."
        url="/blog"
        keywords="reiki blog, holistic health articles, meditation tips, chakra healing blog, spiritual wellness sydney, energy healing insights, kirstie warburton blog"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Blog', path: '/blog' }]}
      />

      <div className="blog-page">
        <section className="blog-hero">
          <div className="container">
            <h1>Blog</h1>
            <p className="blog-hero__subtitle">
              Insights, guidance and inspiration for your holistic wellness journey
            </p>
          </div>
        </section>

        <section className="section blog-content">
          <div className="container">
            <div className="blog-grid">
              {blogPosts.map((post) => (
                <Link to={`/blog/${post.slug}`} key={post.slug} className="blog-card-link">
                  <Card className="blog-card">
                    <div className="blog-card__image">
                      <img src={post.image} alt={post.title} loading="lazy" />
                    </div>
                    <div className="blog-card__content">
                      <span className="blog-card__category">{post.category}</span>
                      <h2 className="blog-card__title">{post.title}</h2>
                      <p className="blog-card__excerpt">{post.excerpt}</p>
                      <div className="blog-card__meta">
                        <span className="blog-card__date">{formatDate(post.date)}</span>
                        <span className="blog-card__read-more">Read More</span>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (!post) {
    return (
      <div className="blog-page">
        <section className="blog-hero">
          <div className="container">
            <h1>Post Not Found</h1>
            <p className="blog-hero__subtitle">
              This article may have been moved or removed.
            </p>
            <Link to="/blog" className="blog-back-link">
              Back to Blog
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${post.title} | KO Holistic Health Blog`}
        description={post.excerpt}
        url={`/blog/${post.slug}`}
        keywords={`${post.category} blog, holistic health, ${post.title.toLowerCase()}, kirstie warburton, sydney`}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
          { name: post.title, path: `/blog/${post.slug}` }
        ]}
      />

      <div className="blog-page">
        <section className="blog-post-hero">
          <div className="blog-post-hero__image">
            <img src={post.image} alt={post.title} />
            <div className="blog-post-hero__overlay"></div>
          </div>
          <div className="container">
            <div className="blog-post-hero__content">
              <span className="blog-post-hero__category">{post.category}</span>
              <h1>{post.title}</h1>
              <div className="blog-post-hero__meta">
                <span>{formatDate(post.date)}</span>
                <span>By Kirstie Warburton</span>
              </div>
            </div>
          </div>
        </section>

        <section className="section blog-post-content">
          <div className="container">
            <div className="blog-post-layout">
              <article className="blog-post-article">
                {post.content.split('\n\n').map((paragraph, index) => {
                  if (paragraph.startsWith('## ')) {
                    return <h2 key={index}>{paragraph.replace('## ', '')}</h2>;
                  }
                  if (paragraph.startsWith('- ')) {
                    const items = paragraph.split('\n').filter(line => line.startsWith('- '));
                    return (
                      <ul key={index}>
                        {items.map((item, i) => (
                          <li key={i}>{item.replace('- ', '').replace(/\*\*(.*?)\*\*/g, '$1')}</li>
                        ))}
                      </ul>
                    );
                  }
                  if (paragraph.startsWith('1. ')) {
                    const items = paragraph.split('\n').filter(line => /^\d+\./.test(line));
                    return (
                      <ol key={index}>
                        {items.map((item, i) => (
                          <li key={i}>{item.replace(/^\d+\.\s*/, '').replace(/\*\*(.*?)\*\*/g, '$1')}</li>
                        ))}
                      </ol>
                    );
                  }
                  return <p key={index}>{paragraph}</p>;
                })}
              </article>

              <aside className="blog-post-sidebar">
                <Card className="blog-sidebar-card">
                  <h3>Ready to Experience Healing?</h3>
                  <p>Book a session with Kirstie and begin your holistic wellness journey.</p>
                  <Button href="/booking" fullWidth>Book a Session</Button>
                </Card>

                <Card className="blog-sidebar-card">
                  <h3>More Articles</h3>
                  <ul className="blog-sidebar-links">
                    {blogPosts
                      .filter(p => p.slug !== slug)
                      .slice(0, 3)
                      .map(p => (
                        <li key={p.slug}>
                          <Link to={`/blog/${p.slug}`}>{p.title}</Link>
                        </li>
                      ))}
                  </ul>
                </Card>
              </aside>
            </div>

            <div className="blog-post-nav">
              <Link to="/blog" className="blog-back-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="19" y1="12" x2="5" y2="12"/>
                  <polyline points="12 19 5 12 12 5"/>
                </svg>
                Back to Blog
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export { BlogList, BlogPost, blogPosts };
